import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import PublicLayout from '../../components/layout/PublicLayout';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import SearchableSelect from '../../components/common/SearchableSelect';
import { orderAPI, enumAPI, productAPI } from '../../services/api';
import { publicOrderAPI } from '../../services/publicApi';
import RefundPolicyNotice from '../../components/common/RefundPolicyNotice';
import OrderFormDetailsPanel from '../../components/common/OrderFormDetailsPanel';
import { buildClosedRefundMessage } from '../../utils/refundTimeline';
import { useAuth } from '../../context/AuthContext';

const ORDER_STATUS = {
  ORDER_FORM_FILLED: 'ORDER_FORM_FILLED',
  REFUND_FORM_FILLED: 'REFUND_FORM_FILLED',
  APPROVED: 'APPROVED',
  PAID: 'PAID',
  PAID_TO_MEDIATOR: 'PAID_TO_MEDIATOR',
  PAID_TO_BUYER: 'PAID_TO_BUYER',
  REJECTED: 'REJECTED',
  REJECT_AND_REFILL: 'REJECT_AND_REFILL',
  ORDER_FORM_REFILL_REQUEST: 'ORDER_FORM_REFILL_REQUEST',
  REFUND_FORM_REFILL_REQUEST: 'REFUND_FORM_REFILL_REQUEST',
  CANCELLED: 'CANCELLED',
  REFILL_REQUEST: 'REFILL_REQUEST',
};

const RefundForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, getOrdersRoute } = useAuth();
  const [reviewRatingScreenshot, setReviewRatingScreenshot] = useState(null);
  const [deliveredOrderScreenshot, setDeliveredOrderScreenshot] = useState(null);
  const [screenRecording, setScreenRecording] = useState(null);
  const [returnWindowCloseScreenshot, setReturnWindowCloseScreenshot] = useState(null);
  const [sellerFeedbackScreenshot, setSellerFeedbackScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [currentStep, setCurrentStep] = useState('refund-form'); // Default to refund-form, logic below will adjust if needed
  const [orderData, setOrderData] = useState(null);
  const [productData, setProductData] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [showStatusMessage, setShowStatusMessage] = useState(false);
  const [returnDateValid, setReturnDateValid] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);
  const [isRefillMode, setIsRefillMode] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingEnums, setLoadingEnums] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [refundOrderType, setRefundOrderType] = useState('ORIGINAL');
  const [replacementOrderId, setReplacementOrderId] = useState('');
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm();

  // Load initial data and check modes
  React.useEffect(() => {
    const init = async () => {
      setIsInitialLoading(true);
      await loadPaymentMethods();
      
      const params = new URLSearchParams(window.location.search);
      const editId = params.get('edit');
      const refill = params.get('refill') === 'true';
      const orderIdParam = params.get('orderId');
      const orderIdFromState = location.state?.orderId;
      const prefilledOrderId = orderIdParam || orderIdFromState;
      
      if (editId) {
        setIsEditMode(true);
        setIsRefillMode(refill);
        setEditOrderId(editId);
        await loadOrderForEdit(editId);
        setCurrentStep('refund-form');
      } else if (prefilledOrderId) {
        setValue('orderId', prefilledOrderId);
        await lookupOrderById(prefilledOrderId);
      } else {
        setCurrentStep('order-id');
      }
      setIsInitialLoading(false);
    };
    
    init();

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [location.state?.orderId]);

  const loadOrderForEdit = async (id) => {
    try {
      const response = await orderAPI.getOrder(id);
      const order = response.data.data;
      if (order) {
        setOrderData(order);
        // Fetch product details to get screenshot requirements
        await fetchProductDetails(order.productId);
        
        setValue('reviewLink', order.reviewLink);
        setValue('whatsappNumber', order.whatsappNumber);
        setValue('paymentMethod', order.paymentMethod);
        setValue('upiId', order.upiId);
        setValue('accountHolderName', order.accountHolderName);
        setValue('bankAccount', order.bankAccount);
        setValue('bankName', order.bankName);
        setValue('ifscCode', order.ifscCode);
        setValue('returnPeriodOverDate', order.returnPeriodOverDate);
        
        // Auto-fill screenshots if they exist
        if (order.reviewRatingScreenshot) {
          console.log('Setting reviewRatingScreenshot:', order.reviewRatingScreenshot);
          setReviewRatingScreenshot(order.reviewRatingScreenshot);
        }
        if (order.deliveredOrderScreenshot) {
          console.log('Setting deliveredOrderScreenshot:', order.deliveredOrderScreenshot);
          setDeliveredOrderScreenshot(order.deliveredOrderScreenshot);
        }
        if (order.sellerFeedbackScreenshot) {
          console.log('Setting sellerFeedbackScreenshot:', order.sellerFeedbackScreenshot);
          setSellerFeedbackScreenshot(order.sellerFeedbackScreenshot);
        }
        if (order.returnWindowClsoeScreenshot || order.returnWindowCloseScreenshot) {
          const ss = order.returnWindowClsoeScreenshot || order.returnWindowCloseScreenshot;
          console.log('Setting returnWindowCloseScreenshot:', ss);
          setReturnWindowCloseScreenshot(ss);
        }
        if (order.screenRecording) {
          console.log('Setting screenRecording:', order.screenRecording);
          setScreenRecording(order.screenRecording);
        }

        // Move directly to refund form step if editing or refilling
        setCurrentStep('refund-form');
      }
    } catch (error) {
      console.error('Failed to load order for edit:', error);
      toast.error('Failed to load order details');
    }
  };

  const loadPaymentMethods = async () => {
    setLoadingEnums(true);
    try {
      const response = await enumAPI.getPaymentMethod();
      setPaymentMethods(response.data || []);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
      setPaymentMethods([]);
    } finally {
      setLoadingEnums(false);
    }
  };

  const selectedPaymentMethod = watch('paymentMethod');
  const paymentMethodOptions = paymentMethods.map((method) => {
    const value = typeof method === 'string' ? method : method.code;
    const label = typeof method === 'string' ? method : method.label || method.code;
    return { value, label };
  });

  const fetchProductDetails = async (productId) => {
    if (!productId) return;
    
    setLoadingProduct(true);
    try {
      console.log('Fetching product details for:', productId);
      const response = await productAPI.getProduct(productId);
      const product = response.data.data;
      console.log('Product details:', product);
      setProductData(product);
    } catch (error) {
      console.error('Failed to fetch product details:', error);
      toast.error('Failed to load product requirements');
    } finally {
      setLoadingProduct(false);
    }
  };

  const lookupOrderById = async (orderId) => {
    const normalizedOrderId = (orderId || '').trim().replace(/\s+/g, '');
    if (!normalizedOrderId) {
      toast.error('Order ID is required');
      return;
    }

    setSubmitting(true);

    try {
      setValue('orderId', normalizedOrderId);
      const response = await orderAPI.getOrder(normalizedOrderId);
      const order = response.data.data;

      setOrderData(order);
      await fetchProductDetails(order.productId);

      switch (order.orderStatus) {
        case ORDER_STATUS.REFUND_FORM_FILLED:
          setStatusMessage('Refund form is already filled. Please check the status of your order by moving to the track order page.');
          setShowStatusMessage(true);
          break;

        case ORDER_STATUS.PAID:
          setStatusMessage('Amount has already been paid for this order.');
          setShowStatusMessage(true);
          break;

        case ORDER_STATUS.REJECTED:
          setStatusMessage(`Your order has been rejected. ${order.reason ? `Reason: ${order.reason}` : 'Please connect to your mediator for more details.'}`);
          setShowStatusMessage(true);
          break;

        case ORDER_STATUS.CANCELLED:
          setStatusMessage('This order has been cancelled. Please contact support for more information.');
          setShowStatusMessage(true);
          break;

        case ORDER_STATUS.ORDER_FORM_FILLED:
        case ORDER_STATUS.ORDER_FORM_REFILL_REQUEST:
        case ORDER_STATUS.REFUND_FORM_REFILL_REQUEST:
        case ORDER_STATUS.REJECT_AND_REFILL:
        case ORDER_STATUS.REFILL_REQUEST:
          if (order.refundTimeline && !order.refundTimeline.refundFormOpen
              && order.orderStatus !== ORDER_STATUS.REFUND_FORM_REFILL_REQUEST
              && order.orderStatus !== ORDER_STATUS.REJECT_AND_REFILL) {
            setStatusMessage(buildClosedRefundMessage(order.refundTimeline.policy));
            setShowStatusMessage(true);
            break;
          }

          if (order.orderStatus === ORDER_STATUS.REFUND_FORM_REFILL_REQUEST
              || order.orderStatus === ORDER_STATUS.REJECT_AND_REFILL) {
            setIsRefillMode(true);
            setEditOrderId(order.id);
            setStatusMessage(
              order.reason
                ? `Your refund form was rejected. Please refill it. Reason: ${order.reason}`
                : 'Your refund form was rejected. Please refill it with the corrected details.'
            );
            setShowStatusMessage(true);
          } else if (order.refundTimeline?.warningLevel === 'PENALTY_50' || order.refundTimeline?.warningLevel === 'PENALTY_100') {
            setStatusMessage(
              order.refundTimeline.warningMessage ||
              `Late refund: ₹${order.refundTimeline.penaltyAmount} penalty will be deducted from your payout.`
            );
            setShowStatusMessage(true);
          }

          setCurrentStep('refund-order-type');
          break;

        default:
          setStatusMessage('Order status is not valid for refund request.');
          setShowStatusMessage(true);
          break;
      }
    } catch (error) {
      console.error('Order validation failed:', error);
      toast.error(error.response?.data?.message || 'Failed to validate order. Please check your Order ID.');
      setCurrentStep('order-id');
    } finally {
      setSubmitting(false);
    }
  };

  const onOrderIdSubmit = async (data) => {
    setRefundOrderType('ORIGINAL');
    setReplacementOrderId('');
    await lookupOrderById(data.orderId);
  };

  const onRefundOrderTypeSubmit = (e) => {
    e.preventDefault();
    if (refundOrderType === 'REPLACEMENT' && !replacementOrderId.trim()) {
      toast.error('Replacement Order ID is required');
      return;
    }
    setCurrentStep('date-validation');
  };

  const onReturnDateSubmit = (data) => {
    // Validate return period date - if the date is above current date, don't show other fields
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const returnPeriodDate = new Date(data.returnPeriodOverDate);
    returnPeriodDate.setHours(0, 0, 0, 0);
    
    // Reset any previous status messages
    setShowStatusMessage(false);
    
    if (returnPeriodDate < currentDate) {
      // Date is in the past - show other fields for refund form
      setCurrentStep('refund-form');
    } else {
      // Date is today or in the future - don't show other fields
      setStatusMessage('You cannot fill the refund form if the return window is not closed. Please wait until the return period has passed.');
      setShowStatusMessage(true);
    }
  };

  const onSubmit = async (data) => {
    console.log('=== Refund Form Submit Called ===');
    console.log('Form data:', data);
    console.log('Screenshots:', {
      reviewRatingScreenshot: !!reviewRatingScreenshot,
      deliveredOrderScreenshot: !!deliveredOrderScreenshot,
      sellerFeedbackScreenshot: !!sellerFeedbackScreenshot,
      returnWindowCloseScreenshot: !!returnWindowCloseScreenshot,
      screenRecording: !!screenRecording
    });
    console.log('Product data:', productData);
    console.log('Payment method:', data.paymentMethod);
    
    if (!reviewRatingScreenshot || !deliveredOrderScreenshot) {
      console.log('Validation failed: Missing required screenshots');
      toast.error('Please upload both review and delivery screenshots');
      return;
    }

    // Validate conditional screenshots
    if (productData?.sellerFeedback && !sellerFeedbackScreenshot) {
      console.log('Validation failed: Missing seller feedback screenshot');
      toast.error('Please upload seller feedback screenshot');
      return;
    }
    if (productData?.returnWindowClose && !returnWindowCloseScreenshot) {
      console.log('Validation failed: Missing return window close screenshot');
      toast.error('Please upload return window close screenshot');
      return;
    }
    if (productData?.screenRecording && !screenRecording) {
      console.log('Validation failed: Missing screen recording');
      toast.error('Please upload screen recording');
      return;
    }

    // Validate payment method fields
    if (data.paymentMethod === 'UPI' && !data.upiId) {
      console.log('Validation failed: Missing UPI ID');
      toast.error('Please enter UPI ID');
      return;
    }
    if (data.paymentMethod === 'ACCOUNT' && (!data.accountHolderName || !data.bankAccount || !data.bankName || !data.ifscCode)) {
      console.log('Validation failed: Missing bank account details');
      toast.error('Please fill all bank account details');
      return;
    }

    console.log('All validations passed, proceeding with submission...');
    setSubmitting(true);
    
    try {
      console.log('=== Creating FormData ===');
      const formData = new FormData();

      const resolvedOrderId =
        orderData?.orderId ??
        data.orderId ??
        new URLSearchParams(window.location.search).get("orderId") ??
        location.state?.orderId;
      if (!resolvedOrderId) {
        toast.error("Order ID is missing. Please verify your order again.");
        return;
      }

      const refundData = {
        id: isEditMode ? editOrderId : null,
        orderId: resolvedOrderId,
        returnPeriodOverDate: data.returnPeriodOverDate,
        reviewLink: data.reviewLink,
        whatsappNumber: data.whatsappNumber?.trim() || null,
        paymentMethod: data.paymentMethod,
        upiId: data.paymentMethod === 'UPI' ? data.upiId : null,
        accountHolderName: data.paymentMethod === 'ACCOUNT' ? data.accountHolderName : null,
        bankAccount: data.paymentMethod === 'ACCOUNT' ? data.bankAccount : null,
        bankName: data.paymentMethod === 'ACCOUNT' ? data.bankName : null,
        ifscCode: data.paymentMethod === 'ACCOUNT' ? data.ifscCode : null,
        orderStatus: 'REFUND_FORM_FILLED',
        oldOrderId: refundOrderType === 'REPLACEMENT' ? replacementOrderId.trim() : null,
      };

      formData.append(
        'data',
        new Blob(
          [JSON.stringify(refundData)],
          { type: 'application/json' }
        )
      );

      formData.append('reviewRatingScreenshot', reviewRatingScreenshot);
      formData.append('deliveredOrderScreenshot', deliveredOrderScreenshot);

      if (productData?.sellerFeedback && sellerFeedbackScreenshot) {
        formData.append('sellerFeedbackScreenshot', sellerFeedbackScreenshot);
      }

      if (productData?.returnWindowClose && returnWindowCloseScreenshot) {
        formData.append('returnWindowCloseScreenshot', returnWindowCloseScreenshot);
      }

      if (productData?.screenRecording && screenRecording) {
        formData.append('screenRecording', screenRecording);
      }

      // Make the API call to match the curl endpoint
      console.log('=== Making API Call ===');
      // Always use submitRefund so penalty + status transitions stay consistent for first fill and refill.
      const response = await publicOrderAPI.submitRefund(formData);

      // Backend success payload often has null/omitted data — never assume response.data.data exists.
      const apiBody = response?.data ?? null;
      const result = apiBody?.data ?? null;
      const submittedOrderId = result?.orderId ?? resolvedOrderId ?? null;

      console.log('API Response:', response, 'orderId:', submittedOrderId);
      toast.success('Refund request submitted successfully! A confirmation email has been sent with your order details.');
      navigate('/track');
    } catch (error) {
      console.error('=== API Call Failed ===');
      console.error('Full error object:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      console.error('Error config:', error.config);
      
      // Show more specific error message
      let errorMessage = 'Failed to submit refund request';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        console.log('Using backend error message:', errorMessage);
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        console.log('Using backend error:', errorMessage);
      } else if (error.message) {
        errorMessage = error.message;
        console.log('Using error message:', errorMessage);
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to render screenshot upload component
  const renderScreenshot = (title, screenshot, setScreenshot, id, icon) => (
    <div>
      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
        {title} *
      </label>
      <div style={{
        border: '2px dashed var(--border)',
        borderRadius: '8px',
        padding: '20px',
        textAlign: 'center',
        background: 'var(--bg-deep)',
        minHeight: '150px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {screenshot ? (
          <div style={{ width: '100%' }}>
            <img 
              src={typeof screenshot === 'string' ? screenshot : URL.createObjectURL(screenshot)} 
              alt={title} 
              style={{ maxWidth: '100%', maxHeight: '80px', borderRadius: '4px' }} 
            />
            <button 
              type="button" 
              onClick={() => setScreenshot(null)} 
              style={{ marginTop: '8px', padding: '2px 8px', background: 'var(--red)', color: 'white', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}
            >
              Remove
            </button>
          </div>
        ) : (
          <>
            <input 
              type="file" 
              accept="image/*" 
              onChange={(e) => setScreenshot(e.target.files[0])} 
              style={{ display: 'none' }} 
              id={id} 
            />
            <label htmlFor={id} style={{ cursor: 'pointer' }}>
              <div style={{ fontSize: '32px', marginBottom: '4px' }}>{icon}</div>
              <p style={{ fontSize: '12px', color: 'var(--text)' }}>Upload {title}</p>
            </label>
          </>
        )}
      </div>
    </div>
  );

  return (
    <PublicLayout title="Refund Form" showBack onBackClick={() => {
      if (isAuthenticated) {
        navigate(getOrdersRoute());
      } else {
        navigate('/');
      }
    }}>
      <div style={{ maxWidth: '896px', margin: '0 auto' }}>
        {showBanner && (
          <div style={{ marginBottom: '16px' }}>
            <RefundPolicyNotice policy={orderData?.refundTimeline?.policy} />
          </div>
        )}

        {/* Banner */}
        {showBanner && (
          <div 
            style={{
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: 'var(--orange-light)',
              border: `1px solid var(--orange)33`,
            }}
          >
            <Icon name="info" size={16} color="var(--orange)" />
            <span style={{ 
              flex: '1', 
              fontSize: '14px', 
              fontWeight: '500', 
              color: 'var(--orange)' 
            }}>
              Fill in your order details and upload the refund screenshot for quick processing.
            </span>
            <button
              onClick={() => setShowBanner(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--orange)',
                cursor: 'pointer'
              }}
            >
              <Icon name="x" size={13} />
            </button>
          </div>
        )}

        {/* Main Form */}
        <div className="card" style={{ overflow: 'hidden' }}>
          {/* Header */}
          <div 
            style={{
              padding: '24px',
              textAlign: 'center',
              background: 'linear-gradient(135deg, var(--orange), #DC2626)',
            }}
          >
            <h2 
              className="outfit"
              style={{ 
                fontWeight: '700', 
                fontSize: '24px', 
                marginBottom: '8px',
                color: 'white' 
              }}
            >
              Refund Request
            </h2>
            <p 
              style={{ 
                fontSize: '14px',
                color: 'rgba(255,255,255,0.9)' 
              }}
            >
              Submit your refund request with order details
            </p>
          </div>

          <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-card)' }}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/guides')}
              style={{ width: '100%', justifyContent: 'center' }}
              icon="info"
            >
              View Demo Screenshots
            </Button>
          </div>

          {/* Form Content */}
          <div style={{ padding: '24px' }}>
            {isInitialLoading && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>⌛</div>
                <p>Loading your order details...</p>
              </div>
            )}

            {/* Order ID Step */}
            {!isInitialLoading && currentStep === 'order-id' && (
              <form onSubmit={handleSubmit(onOrderIdSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <RefundPolicyNotice compact policy={orderData?.refundTimeline?.policy} />
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Order ID *</label>
                  <input
                    {...register('orderId', { 
                      required: 'Order ID is required'
                    })}
                    type="text"
                    placeholder="Enter your Order ID"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      background: 'var(--bg)',
                      color: 'var(--text)',
                      borderColor: errors.orderId ? 'var(--red)' : 'var(--border)'
                    }}
                  />
                  {errors.orderId && (
                    <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.orderId.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  loading={submitting}
                  style={{
                    padding: '14px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '8px'
                  }}
                >
                  {submitting ? 'Validating...' : 'Check Order'}
                </Button>
              </form>
            )}

            {/* Status Message */}
            {showStatusMessage && (
              <div 
                style={{
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: 'var(--orange-light)',
                  border: `1px solid var(--orange)33`,
                }}
              >
                <Icon name="info" size={16} color="var(--orange)" />
                <span style={{ 
                  flex: '1', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: 'var(--orange)' 
                }}>
                  {statusMessage}
                </span>
                <button
                  onClick={() => setShowStatusMessage(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--orange)',
                    cursor: 'pointer'
                  }}
                >
                  <Icon name="x" size={13} />
                </button>
              </div>
            )}

            {/* Track Order Link */}
            {showStatusMessage && orderData?.orderStatus === ORDER_STATUS.REFUND_FORM_FILLED && (
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <Button
                  onClick={() => navigate('/track')}
                  style={{
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    borderRadius: '8px'
                  }}
                >
                  Go to Track Order
                </Button>
              </div>
            )}

            {/* Refund order type step — after order ID validated */}
            {!isInitialLoading && currentStep === 'refund-order-type' && (
              <form onSubmit={onRefundOrderTypeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <OrderFormDetailsPanel order={orderData} product={productData} />
                <RefundPolicyNotice compact policy={orderData?.refundTimeline?.policy} />

                <div>
                  <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
                    Are you filling refund for *
                  </label>
                  <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="refundOrderType"
                        value="ORIGINAL"
                        checked={refundOrderType === 'ORIGINAL'}
                        onChange={() => setRefundOrderType('ORIGINAL')}
                        style={{ width: '18px', height: '18px', accentColor: 'var(--primary)' }}
                      />
                      <span style={{ fontSize: '14px', color: 'var(--text)' }}>Original order</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="refundOrderType"
                        value="REPLACEMENT"
                        checked={refundOrderType === 'REPLACEMENT'}
                        onChange={() => setRefundOrderType('REPLACEMENT')}
                        style={{ width: '18px', height: '18px', accentColor: 'var(--primary)' }}
                      />
                      <span style={{ fontSize: '14px', color: 'var(--text)' }}>Replacement order</span>
                    </label>
                  </div>
                </div>

                {refundOrderType === 'REPLACEMENT' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
                      Replacement Order ID *
                    </label>
                    <input
                      type="text"
                      value={replacementOrderId}
                      onChange={(e) => setReplacementOrderId(e.target.value)}
                      placeholder="Enter the replacement order ID"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        background: 'var(--bg)',
                        color: 'var(--text)',
                      }}
                    />
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Enter the new order ID if this refund is for a replacement order.
                    </p>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px' }}>
                  <Button
                    type="button"
                    onClick={() => {
                      setCurrentStep('order-id');
                      reset();
                      setOrderData(null);
                      setRefundOrderType('ORIGINAL');
                      setReplacementOrderId('');
                    }}
                    variant="secondary"
                    style={{ padding: '14px 24px', fontSize: '16px', fontWeight: '600', borderRadius: '8px' }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    style={{ padding: '14px 24px', fontSize: '16px', fontWeight: '600', borderRadius: '8px', flex: 1 }}
                  >
                    Continue
                  </Button>
                </div>
              </form>
            )}

            {/* Date Validation Step */}
            {!isInitialLoading && currentStep === 'date-validation' && (
              <form onSubmit={handleSubmit(onReturnDateSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <OrderFormDetailsPanel order={orderData} product={productData} />
                <RefundPolicyNotice compact policy={orderData?.refundTimeline?.policy} />

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Return Window Closed Date *</label>
                  <input
                    {...register('returnPeriodOverDate', { required: 'Return window closed date is required' })}
                    type="date"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      background: 'var(--bg)',
                      color: 'var(--text)',
                      borderColor: errors.returnPeriodOverDate ? 'var(--red)' : 'var(--border)'
                    }}
                  />
                  {errors.returnPeriodOverDate && (
                    <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.returnPeriodOverDate.message}</p>
                  )}
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    Please select the date when your return window closed on the platform.
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <Button
                    type="button"
                    onClick={() => {
                      setCurrentStep('refund-order-type');
                      setShowStatusMessage(false);
                    }}
                    variant="secondary"
                    style={{
                      padding: '14px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      borderRadius: '8px'
                    }}
                  >
                    Back
                  </Button>

                  <Button
                    type="submit"
                    disabled={submitting}
                    loading={submitting}
                    style={{
                      padding: '14px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      flex: 1
                    }}
                  >
                    {submitting ? 'Validating...' : 'Continue to Refund Form'}
                  </Button>
                </div>
              </form>
            )}

            {/* Refund Form Step */}
            {!isInitialLoading && currentStep === 'refund-form' && (
              <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <OrderFormDetailsPanel order={orderData} product={productData} />
                <RefundPolicyNotice compact policy={orderData?.refundTimeline?.policy} />

                {/* Review Link - Full Width */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Review Link *</label>
                  <input
                    {...register('reviewLink', { required: 'Review link is required' })}
                    type="url"
                    placeholder="https://amazon.com/review/ABC123"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      background: 'var(--bg)',
                      color: 'var(--text)',
                      borderColor: errors.reviewLink ? 'var(--red)' : 'var(--border)'
                    }}
                  />
                  {errors.reviewLink && (
                    <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.reviewLink.message}</p>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>WhatsApp Number <span style={{ color: 'var(--text-sub)', fontWeight: 400 }}>(optional)</span></label>
                  <div style={{ display: 'flex' }}>
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 12px',
                      background: 'var(--bg-deep)',
                      border: '1px solid var(--border)',
                      borderRight: 'none',
                      borderRadius: '8px 0 0 8px',
                      fontSize: '14px',
                      color: 'var(--text-sub)'
                    }}>+91</span>
                    <input
                      {...register('whatsappNumber', {
                        validate: (value) => {
                          if (!value || !String(value).trim()) return true;
                          return /^[0-9]{10}$/.test(String(value).trim()) || 'Invalid mobile number (10 digits)';
                        },
                      })}
                      placeholder="9876543210"
                      style={{
                        flex: '1',
                        padding: '12px',
                        border: '1px solid var(--border)',
                        borderRadius: '0 8px 8px 0',
                        background: 'var(--bg)',
                        color: 'var(--text)',
                        borderColor: errors.whatsappNumber ? 'var(--red)' : 'var(--border)'
                      }}
                    />
                  </div>
                  {errors.whatsappNumber && (
                    <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.whatsappNumber.message}</p>
                  )}
                </div>

                {/* Payment Method */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Payment Method *</label>
                  <input type="hidden" {...register('paymentMethod', { required: 'Please select payment method' })} />
                  <SearchableSelect
                    options={paymentMethodOptions}
                    value={selectedPaymentMethod}
                    onChange={(value) => setValue('paymentMethod', value, { shouldValidate: true, shouldDirty: true })}
                    placeholder={loadingEnums ? 'Loading payment methods...' : 'Select payment method'}
                    searchPlaceholder="Type payment method..."
                    error={Boolean(errors.paymentMethod)}
                    disabled={loadingEnums}
                    noOptionsText="No payment methods available"
                    disabledText="Payment methods are loading"
                  />
                  {errors.paymentMethod && (
                    <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.paymentMethod.message}</p>
                  )}
                </div>

                {/* Payment Details - Conditional */}
                {selectedPaymentMethod === 'UPI' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>UPI ID *</label>
                    <input
                      {...register('upiId', { required: 'UPI ID is required' })}
                      type="text"
                      placeholder="priya@upi"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        background: 'var(--bg)',
                        color: 'var(--text)',
                        borderColor: errors.upiId ? 'var(--red)' : 'var(--border)'
                      }}
                    />
                    {errors.upiId && (
                      <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.upiId.message}</p>
                    )}
                  </div>
                )}

                {selectedPaymentMethod === 'ACCOUNT' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Account Holder Name *</label>
                      <input
                        {...register('accountHolderName', { required: 'Account holder name is required' })}
                        type="text"
                        placeholder="Priya Kathpal"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          background: 'var(--bg)',
                          color: 'var(--text)',
                          borderColor: errors.accountHolderName ? 'var(--red)' : 'var(--border)'
                        }}
                      />
                      {errors.accountHolderName && (
                        <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.accountHolderName.message}</p>
                      )}
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Bank Account Number *</label>
                      <input
                        {...register('bankAccount', { required: 'Bank account number is required' })}
                        type="text"
                        placeholder="1234567890123456"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          background: 'var(--bg)',
                          color: 'var(--text)',
                          borderColor: errors.bankAccount ? 'var(--red)' : 'var(--border)'
                        }}
                      />
                      {errors.bankAccount && (
                        <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.bankAccount.message}</p>
                      )}
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Bank Name *</label>
                      <input
                        {...register('bankName', { required: 'Bank name is required' })}
                        type="text"
                        placeholder="State Bank of India"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          background: 'var(--bg)',
                          color: 'var(--text)',
                          borderColor: errors.bankName ? 'var(--red)' : 'var(--border)'
                        }}
                      />
                      {errors.bankName && (
                        <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.bankName.message}</p>
                      )}
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>IFSC Code *</label>
                      <input
                        {...register('ifscCode', { required: 'IFSC code is required' })}
                        type="text"
                        placeholder="SBIN0001234"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          background: 'var(--bg)',
                          color: 'var(--text)',
                          borderColor: errors.ifscCode ? 'var(--red)' : 'var(--border)'
                        }}
                      />
                      {errors.ifscCode && (
                        <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.ifscCode.message}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Screenshot Uploads */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Review Rating Screenshot *</label>
                    <div style={{
                      border: '2px dashed var(--border)',
                      borderRadius: '8px',
                      padding: '24px',
                      textAlign: 'center',
                      background: 'var(--bg-deep)',
                      minHeight: '180px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      {reviewRatingScreenshot ? (
                        <div style={{ width: '100%' }}>
                          <img 
                            src={typeof reviewRatingScreenshot === 'string' ? reviewRatingScreenshot : URL.createObjectURL(reviewRatingScreenshot)} 
                            alt="Review screenshot" 
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '120px', 
                              borderRadius: '4px',
                              objectFit: 'contain'
                            }} 
                          />
                          <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                            {typeof reviewRatingScreenshot === 'string' ? 'Current Screenshot' : reviewRatingScreenshot.name}
                          </p>
                          <button
                            type="button"
                            onClick={() => setReviewRatingScreenshot(null)}
                            style={{
                              marginTop: '8px',
                              padding: '4px 8px',
                              background: 'var(--red)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setReviewRatingScreenshot(e.target.files[0])}
                            style={{ display: 'none' }}
                            id="review-screenshot"
                          />
                          <label htmlFor="review-screenshot" style={{ cursor: 'pointer', width: '100%' }}>
                            <div style={{ fontSize: '48px', marginBottom: '8px' }}>📷</div>
                            <p style={{ color: 'var(--text)', marginBottom: '4px' }}>
                              Click to upload Review Screenshot
                            </p>
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                              PNG, JPG up to 10MB
                            </p>
                          </label>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Delivered Order Screenshot *</label>
                    <div style={{
                      border: '2px dashed var(--border)',
                      borderRadius: '8px',
                      padding: '24px',
                      textAlign: 'center',
                      background: 'var(--bg-deep)',
                      minHeight: '180px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      {deliveredOrderScreenshot ? (
                        <div style={{ width: '100%' }}>
                          <img 
                            src={typeof deliveredOrderScreenshot === 'string' ? deliveredOrderScreenshot : URL.createObjectURL(deliveredOrderScreenshot)} 
                            alt="Delivery screenshot" 
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '120px', 
                              borderRadius: '4px',
                              objectFit: 'contain'
                            }} 
                          />
                          <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                            {typeof deliveredOrderScreenshot === 'string' ? 'Current Screenshot' : deliveredOrderScreenshot.name}
                          </p>
                          <button
                            type="button"
                            onClick={() => setDeliveredOrderScreenshot(null)}
                            style={{
                              marginTop: '8px',
                              padding: '4px 8px',
                              background: 'var(--red)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setDeliveredOrderScreenshot(e.target.files[0])}
                            style={{ display: 'none' }}
                            id="delivery-screenshot"
                          />
                          <label htmlFor="delivery-screenshot" style={{ cursor: 'pointer', width: '100%' }}>
                            <div style={{ fontSize: '48px', marginBottom: '8px' }}>📦</div>
                            <p style={{ color: 'var(--text)', marginBottom: '4px' }}>
                              Click to upload Delivery Screenshot
                            </p>
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                              PNG, JPG up to 10MB
                            </p>
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Optional Screenshots */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  {productData?.sellerFeedback && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Seller Feedback Screenshot *</label>
                      <div style={{
                        border: '2px dashed var(--border)',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                        background: 'var(--bg-deep)',
                        minHeight: '150px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {sellerFeedbackScreenshot ? (
                          <div style={{ width: '100%' }}>
                            <img 
                              src={typeof sellerFeedbackScreenshot === 'string' ? sellerFeedbackScreenshot : URL.createObjectURL(sellerFeedbackScreenshot)} 
                              alt="Seller feedback" 
                              style={{ maxWidth: '100%', maxHeight: '80px', borderRadius: '4px' }} 
                            />
                            <button 
                              type="button" 
                              onClick={() => setSellerFeedbackScreenshot(null)} 
                              style={{ marginTop: '8px', padding: '2px 8px', background: 'var(--red)', color: 'white', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <>
                            <input 
                              type="file" 
                              accept="image/*" 
                              onChange={(e) => setSellerFeedbackScreenshot(e.target.files[0])} 
                              style={{ display: 'none' }} 
                              id="seller-feedback" 
                            />
                            <label htmlFor="seller-feedback" style={{ cursor: 'pointer' }}>
                              <div style={{ fontSize: '32px', marginBottom: '4px' }}>💬</div>
                              <p style={{ fontSize: '12px', color: 'var(--text)' }}>Upload Seller Feedback</p>
                            </label>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {productData?.returnWindowClose && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Return Window Close Screenshot *</label>
                      <div style={{
                        border: '2px dashed var(--border)',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                        background: 'var(--bg-deep)',
                        minHeight: '150px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {returnWindowCloseScreenshot ? (
                          <div style={{ width: '100%' }}>
                            <img 
                              src={typeof returnWindowCloseScreenshot === 'string' ? returnWindowCloseScreenshot : URL.createObjectURL(returnWindowCloseScreenshot)} 
                              alt="Return window close" 
                              style={{ maxWidth: '100%', maxHeight: '80px', borderRadius: '4px' }} 
                            />
                            <button 
                              type="button" 
                              onClick={() => setReturnWindowCloseScreenshot(null)} 
                              style={{ marginTop: '8px', padding: '2px 8px', background: 'var(--red)', color: 'white', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <>
                            <input 
                              type="file" 
                              accept="image/*" 
                              onChange={(e) => setReturnWindowCloseScreenshot(e.target.files[0])} 
                              style={{ display: 'none' }} 
                              id="return-window-close" 
                            />
                            <label htmlFor="return-window-close" style={{ cursor: 'pointer' }}>
                              <div style={{ fontSize: '32px', marginBottom: '4px' }}>📅</div>
                              <p style={{ fontSize: '12px', color: 'var(--text)' }}>Upload Return Window Close</p>
                            </label>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Screen Recording */}
                {productData?.screenRecording && (
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Screen Recording *</label>
                    <div style={{ 
                      border: '2px dashed var(--border)', 
                      borderRadius: '8px', 
                      padding: '24px', 
                      textAlign: 'center', 
                      background: 'var(--bg-deep)', 
                      minHeight: '180px', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      {screenRecording ? (
                        <div style={{ width: '100%' }}>
                          <div style={{ background: '#f1f5f9', padding: '12px', borderRadius: '8px', fontSize: '12px', marginBottom: '8px' }}>
                            {typeof screenRecording === 'string' ? 'Current Recording' : screenRecording?.name}
                          </div>
                          <button 
                            type="button" 
                            onClick={() => setScreenRecording(null)} 
                            style={{ padding: '4px 8px', background: 'var(--red)', color: 'white', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <>
                          <input 
                            type="file" 
                            accept="video/*" 
                            onChange={(e) => setScreenRecording(e.target.files[0])} 
                            style={{ display: 'none' }} 
                            id="screen-recording" 
                          />
                          <label htmlFor="screen-recording" style={{ cursor: 'pointer' }}>
                            <div style={{ fontSize: '48px', marginBottom: '8px' }}>🎥</div>
                            <p style={{ fontSize: '12px', color: 'var(--text)' }}>Click to upload Recording</p>
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <Button
                    type="button"
                    onClick={() => {
                      if (isRefillMode || isEditMode) {
                        if (isAuthenticated) {
                          navigate(getOrdersRoute());
                        } else {
                          navigate('/');
                        }
                      } else {
                        setCurrentStep('date-validation');
                      }
                    }}
                    variant="secondary"
                    style={{
                      padding: '14px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      borderRadius: '8px'
                    }}
                  >
                    Back
                  </Button>

                  <Button
                    type="submit"
                    disabled={submitting || !reviewRatingScreenshot || !deliveredOrderScreenshot || 
                             (productData?.sellerFeedback && !sellerFeedbackScreenshot) ||
                             (productData?.returnWindowClose && !returnWindowCloseScreenshot) ||
                             (productData?.screenRecording && !screenRecording)}
                    loading={submitting}
                    style={{
                      padding: '14px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      flex: 1
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Refund Request'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default RefundForm;
